import { useEffect, useState } from 'react';
import { THEME_STORAGE_KEY } from '../ThemeRoot';

function readMode(): 'light' | 'dark' {
  if (typeof document === 'undefined') return 'dark';
  const attr = document.documentElement.dataset.theme;
  if (attr === 'light' || attr === 'dark') return attr;
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored === 'light') return 'light';
  } catch {}
  return 'dark';
}

interface LogoProps {
  height?: number;
  alt?: string;
  className?: string;
  style?: React.CSSProperties;
  variant?: 'auto' | 'light' | 'dark' | 'mono' | 'color';
}

const SRC_MAP: Record<NonNullable<LogoProps['variant']>, string> = {
  auto: '',
  light: '/logo-icon.svg',
  dark: '/logo-icon-white.svg',
  mono: '/logo-icon.svg',
  color: '/logo-icon.svg',
};

const Logo: React.FC<LogoProps> = ({
  height = 28,
  alt = 'Genvas',
  className,
  style,
  variant = 'auto',
}) => {
  const [mode, setMode] = useState<'light' | 'dark'>(() => readMode());

  useEffect(() => {
    if (variant !== 'auto') return;
    const update = () => setMode(readMode());
    update();
    const obs = new MutationObserver(update);
    obs.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme'],
    });
    return () => obs.disconnect();
  }, [variant]);

  const isSplit =
    variant === 'auto' || variant === 'light' || variant === 'dark';
  const useDark = variant === 'auto' ? mode === 'dark' : variant === 'dark';
  if (!isSplit) {
    return (
      <img
        src={SRC_MAP[variant]}
        alt={alt}
        className={className}
        draggable={false}
        style={{ height, width: 'auto', display: 'block', ...style }}
      />
    );
  }
  const iconSrc = useDark ? '/logo-icon-white.svg' : '/logo-icon.svg';
  // `height` is the visual brand height, not just the wordmark height.
  // The old 0.72 mark ratio made the circular Genvas symbol look too small
  // next to the text, especially in auth modals and nested route headers.
  const iconHeight = Math.round(height * 0.92);
  return (
    <div
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: Math.round(height * 0.24),
        ...style,
      }}
    >
      <img
        src={iconSrc}
        alt={alt}
        draggable={false}
        style={{ height: iconHeight, width: 'auto', display: 'block' }}
      />
      <span
        aria-hidden="true"
        style={{
          color: useDark ? '#ffffff' : '#111827',
          fontSize: Math.round(height * 0.7),
          fontWeight: 760,
          letterSpacing: -0.6,
          lineHeight: 1,
        }}
      >
        Genvas
      </span>
    </div>
  );
};

export default Logo;
